import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

type ProductTypeFilter = 'GENERAL' | 'PRESALE' | 'EVENT' | 'SET' | 'CONCERT';
export type ProductSaleStatusFilter = 'ON_SALE' | 'SOLD_OUT' | 'PRESALE' | 'SALE_ENDED' | 'HIDDEN' | 'PREPARING';

export interface ProductFiltersState {
  keyword: string;
  // QA-374 — 판매상태 다중선택(체크박스). 1개면 BE saleStatus, 여러 개는 saleStatuses(BE 반영 필요).
  saleStatuses: ReadonlyArray<ProductSaleStatusFilter>;
  productType: ProductTypeFilter | null;
  categoryId: string | null;
  priceMin: number | null;
  priceMax: number | null;
  stockMin: number | null;
  stockMax: number | null;
  createdFrom: string | null;
  createdTo: string | null;
  // QA-374 — 수정일 범위(yyyy-MM-dd). BE updatedFrom/updatedTo 반영 필요.
  updatedFrom: string | null;
  updatedTo: string | null;
  artistId: string | null;
  hasArtist: boolean | null;
  displayed: boolean | null;
  // QA-89(ID89) 조회조건 확장 — 상품코드/바코드 정확일치 검색.
  productCode: string | null;
  barcode: string | null;
  // 자체상품코드(앨범별 고정 코드) 정확일치 — 한 코드에 여러 상품이 걸린다.
  selfProductCode: string | null;
  page: number;
  size: number;
  sort: string;
  // ID88: 다중 상품 선택 — 일괄삭제(footer) 및 복사(=1건 선택)의 단일 선택 소스.
  // 서버 상태가 아니라 UI 선택 상태이므로 이 store 에 둔다. 필터/페이지 변경 시 자동 초기화.
  selectedIds: ReadonlyArray<string>;
  setKeyword: (keyword: string) => void;
  setSaleStatuses: (saleStatuses: ReadonlyArray<ProductSaleStatusFilter>) => void;
  setProductType: (productType: ProductTypeFilter | null) => void;
  setCategoryId: (categoryId: string | null) => void;
  setPriceMin: (priceMin: number | null) => void;
  setPriceMax: (priceMax: number | null) => void;
  setStockMin: (stockMin: number | null) => void;
  setStockMax: (stockMax: number | null) => void;
  setCreatedFrom: (createdFrom: string | null) => void;
  setCreatedTo: (createdTo: string | null) => void;
  setUpdatedFrom: (updatedFrom: string | null) => void;
  setUpdatedTo: (updatedTo: string | null) => void;
  setArtistId: (artistId: string | null) => void;
  setHasArtist: (hasArtist: boolean | null) => void;
  setDisplayed: (displayed: boolean | null) => void;
  setProductCode: (productCode: string | null) => void;
  setBarcode: (barcode: string | null) => void;
  setSelfProductCode: (selfProductCode: string | null) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  setSort: (sort: string) => void;
  // ID88: 선택 토글(단건), 전체 선택/해제(현재 페이지 id 목록), 전체 해제.
  toggleSelected: (productId: string) => void;
  setSelected: (productIds: ReadonlyArray<string>) => void;
  clearSelection: () => void;
  reset: () => void;
}

const INITIAL: Omit<
  ProductFiltersState,
  | 'setKeyword'
  | 'setSaleStatuses'
  | 'setProductType'
  | 'setCategoryId'
  | 'setPriceMin'
  | 'setPriceMax'
  | 'setStockMin'
  | 'setStockMax'
  | 'setCreatedFrom'
  | 'setCreatedTo'
  | 'setUpdatedFrom'
  | 'setUpdatedTo'
  | 'setArtistId'
  | 'setHasArtist'
  | 'setDisplayed'
  | 'setProductCode'
  | 'setBarcode'
  | 'setSelfProductCode'
  | 'setPage'
  | 'setSize'
  | 'setSort'
  | 'toggleSelected'
  | 'setSelected'
  | 'clearSelection'
  | 'reset'
> = {
  keyword: '',
  saleStatuses: [],
  productType: null,
  categoryId: null,
  priceMin: null,
  priceMax: null,
  stockMin: null,
  stockMax: null,
  createdFrom: null,
  createdTo: null,
  updatedFrom: null,
  updatedTo: null,
  artistId: null,
  hasArtist: null,
  displayed: null,
  productCode: null,
  barcode: null,
  selfProductCode: null,
  page: 1,
  size: 20,
  sort: 'createdAt,DESC',
  selectedIds: [],
};

/**
 * UI filter state for the product list. Server state lives in TanStack Query;
 * this store holds only the search/filter/sort inputs that drive the query key.
 * Subscribers MUST use selectors (`useProductFiltersStore((s) => s.x)`) —
 * destructure-style consumption is forbidden by §4 to avoid re-render storms.
 */
export const useProductFiltersStore = create<ProductFiltersState>()(
  devtools(
    (set) => ({
      ...INITIAL,
      // 필터/페이지/정렬 변경은 보고 있던 목록이 바뀌므로 선택을 비운다(미노출 행 일괄삭제 방지).
      setKeyword: (keyword) => set({ keyword, page: 1, selectedIds: [] }, false, 'setKeyword'),
      setSaleStatuses: (saleStatuses) => set({ saleStatuses, page: 1, selectedIds: [] }, false, 'setSaleStatuses'),
      setProductType: (productType) =>
        set({ productType, page: 1, selectedIds: [] }, false, 'setProductType'),
      setCategoryId: (categoryId) => set({ categoryId, page: 1, selectedIds: [] }, false, 'setCategoryId'),
      setPriceMin: (priceMin) => set({ priceMin, page: 1, selectedIds: [] }, false, 'setPriceMin'),
      setPriceMax: (priceMax) => set({ priceMax, page: 1, selectedIds: [] }, false, 'setPriceMax'),
      setStockMin: (stockMin) => set({ stockMin, page: 1, selectedIds: [] }, false, 'setStockMin'),
      setStockMax: (stockMax) => set({ stockMax, page: 1, selectedIds: [] }, false, 'setStockMax'),
      setCreatedFrom: (createdFrom) => set({ createdFrom, page: 1, selectedIds: [] }, false, 'setCreatedFrom'),
      setCreatedTo: (createdTo) => set({ createdTo, page: 1, selectedIds: [] }, false, 'setCreatedTo'),
      setUpdatedFrom: (updatedFrom) => set({ updatedFrom, page: 1, selectedIds: [] }, false, 'setUpdatedFrom'),
      setUpdatedTo: (updatedTo) => set({ updatedTo, page: 1, selectedIds: [] }, false, 'setUpdatedTo'),
      setArtistId: (artistId) => set({ artistId, page: 1, selectedIds: [] }, false, 'setArtistId'),
      setHasArtist: (hasArtist) => set({ hasArtist, page: 1, selectedIds: [] }, false, 'setHasArtist'),
      setDisplayed: (displayed) => set({ displayed, page: 1, selectedIds: [] }, false, 'setDisplayed'),
      setProductCode: (productCode) => set({ productCode, page: 1, selectedIds: [] }, false, 'setProductCode'),
      setBarcode: (barcode) => set({ barcode, page: 1, selectedIds: [] }, false, 'setBarcode'),
      setSelfProductCode: (selfProductCode) =>
        set({ selfProductCode, page: 1, selectedIds: [] }, false, 'setSelfProductCode'),
      setPage: (page) => set({ page, selectedIds: [] }, false, 'setPage'),
      setSize: (size) => set({ size, page: 1, selectedIds: [] }, false, 'setSize'),
      setSort: (sort) => set({ sort, page: 1, selectedIds: [] }, false, 'setSort'),
      // ID88: 선택 상태는 불변 업데이트(toggle = 있으면 제거, 없으면 추가).
      toggleSelected: (productId) =>
        set(
          (s) => ({
            selectedIds: s.selectedIds.includes(productId)
              ? s.selectedIds.filter((id) => id !== productId)
              : [...s.selectedIds, productId],
          }),
          false,
          'toggleSelected',
        ),
      setSelected: (productIds) => set({ selectedIds: [...productIds] }, false, 'setSelected'),
      clearSelection: () => set({ selectedIds: [] }, false, 'clearSelection'),
      reset: () => set({ ...INITIAL }, false, 'reset'),
    }),
    { name: 'product-filters' },
  ),
);
